import { FC, useEffect } from "react";

import CountryFlag from "@Components/CountryFlag";
import Portal from "@Components/Portal";
import lockBody from "@Utils/lockBody";
import unlockBody from "@Utils/unLockBody";
import wrapperHOC from "@Utils/wrapperHOC";
import { PHONE_1, PHONE_2, PHONE_3, PHONE_4 } from "constants/configConstants";

import useCopy from "../../useCopy";
import styles from "./CallUsDrawer.module.scss";

/**
 * "Call us" — a centred modal listing every student support line (after Headout's
 * "Call us" sheet).
 * Opened from the footer's "Call us" link and the Support menu in the hero bar.
 *
 * The numbers are the site's own support lines (`PHONE_1`–`PHONE_4`, the same four the
 * live footer and header pick between by country). The visitor's own country's line is
 * listed first. Not listed: `PHONE_5` (China, served by the China app's own footer) and
 * `PHONE_7`–`PHONE_9` (the partner lines on /list).
 */
export const SUPPORT_LINES = [
  { country: "GB", name: "United Kingdom", nameKey: "blog.home.regions.uk.name", phone: PHONE_1 },
  { country: "US", name: "United States", nameKey: "blog.home.regions.usa.name", phone: PHONE_3 },
  {
    country: "AU",
    name: "Australia",
    nameKey: "blog.home.regions.australia.name",
    phone: PHONE_4,
  },
  { country: "IN", name: "India", nameKey: "podcast.v2.country.india", phone: PHONE_2 },
];

/** `tel:` wants the number with no spaces (RFC 3966). */
export const telHref = (fullNumber: string) => `tel:${fullNumber.replace(/\s/g, "")}`;

/** The line for a country code, falling back to the UK line as the live footer does. */
export const supportLineFor = (country?: string) =>
  SUPPORT_LINES.find((l) => l.country === country) || SUPPORT_LINES[0];

interface Props {
  onClose: () => void;
  /** The visitor's country code; its line moves to the top. */
  country?: string;
  onCall?: (country: string) => void;
}

const CallUsDrawer: FC<Props> = ({ onClose, country, onCall }) => {
  const copy = useCopy();
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    // The site's own lock (html and body both): this page scrolls on <html>, so hiding
    // the body's overflow alone left the page scrolling behind the modal.
    lockBody();
    return () => {
      document.removeEventListener("keydown", onKey);
      unlockBody();
    };
  }, [onClose]);

  const lines = [...SUPPORT_LINES].sort(
    (a, b) => Number(b.country === country) - Number(a.country === country),
  );

  return (
    <Portal>
      <div className={styles.root}>
        <button
          type="button"
          className={styles.scrim}
          aria-label={copy("Close", "Close")}
          tabIndex={-1}
          onClick={onClose}
        />
        <aside
          className={styles.drawer}
          role="dialog"
          aria-modal="true"
          aria-labelledby="v3-call-us-title"
          data-testid="v3-call-us-drawer"
        >
          <header className={styles.head}>
            <h3 id="v3-call-us-title" className={styles.title}>
              {copy("propertypage.components.chatlinks.callus", "Call us")}
            </h3>
            <button
              type="button"
              className={styles.close}
              aria-label={copy("Close", "Close")}
              onClick={onClose}
            >
              {/* the shared CloseIcon's geometry, at the page's one close size */}
              <svg width="14" height="14" viewBox="0 0 18 18" aria-hidden="true">
                <path
                  d="M1 1l16 16M17 1 1 17"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            </button>
          </header>

          <div className={styles.body}>
            <div className={styles.note}>
              <p className={styles.noteTitle}>
                {copy("home.v3.callus.note.title", "For faster support on a booking:")}
              </p>
              <ul className={styles.noteList}>
                <li>
                  {copy(
                    "home.v3.callus.note.samenumber",
                    "Call from the phone number you used while booking.",
                  )}
                </li>
                <li>
                  {copy(
                    "home.v3.callus.note.othernumber",
                    "Calling from another number? Keep your booking ID ready, with the last four digits of the phone number used for the booking.",
                  )}
                </li>
              </ul>
            </div>

            <ul className={styles.lines}>
              {lines.map((line) => (
                <li key={line.country} className={styles.line}>
                  <span className={styles.country}>
                    <CountryFlag
                      countryCode={line.country}
                      width={20}
                      height={20}
                      className={styles.flag}
                    />
                    {copy(line.nameKey, line.name)}
                  </span>
                  <a
                    href={telHref(line.phone.full_number)}
                    className={styles.number}
                    data-testid={`v3-call-us-${line.country}`}
                    onClick={() => onCall?.(line.country)}
                  >
                    {`${line.phone.country_code} ${line.phone.number}`}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </aside>
      </div>
    </Portal>
  );
};

export default wrapperHOC(CallUsDrawer, {
  componentName: "HomeV3-CallUsDrawer",
  showForChina: true,
});
